import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Settings,
  Megaphone,
  Trash2,
  Users,
  UserPlus,
  UserCheck,
  ChevronDown,
  Eye,
  BookOpen,
} from "lucide-react";
import { cn } from "../lib/cn";
import { nx } from "../lib/onyx-material";
import { BorderGlow, type BorderGlowProps } from "./BorderGlow";
import { Card, CardCover, CardTitle, CardDescription, type CardProps } from "../primitives/Card";
import { Button } from "../primitives/Button";
import { Badge } from "../primitives/Badge";
import { VisibilityBadge } from "./VisibilityBadge";
import { Skeleton } from "../primitives/Skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../primitives/Tooltip";
import { AikoMark } from "./AikoMark";
import { Avatar } from "../primitives/Avatar";
import { AvatarGroup } from "../primitives/AvatarGroup";
import { Image } from "../primitives/Image";
import { Truncate } from "./Truncate";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../primitives/DropdownMenu";

/**
 * StudioCard — community-card-style entity card. Composes the brand `Card`, so
 * it INHERITS the Onyx hex-notch surface from it (issue #158): the `nx-s`
 * notch silhouette, the clip-safe `.elev-card` drop-shadow (lifting to
 * `.elev-panel` on hover), and the continuous `nx-rim` keyline RING. We do NOT
 * re-draw the notch / elevation / keyline here — that would double the rim and
 * the box-shadow channel. Only the loading `.Skeleton` (a surface this file
 * renders directly, not via `Card`) carries the notch itself. Layout:
 *
 *   ┌─ outer card · nx-s notch · elev-card drop-shadow · nx-rim keyline ─┐
 *   │  ┌─ 16:9 image, top-flush ──────────────────────┐ │
 *   │  │  [visibility]                  [premium]      │ │
 *   │  │              (gradient veil bottom)           │ │
 *   │  │      [logo float, bottom-left]                │ │
 *   │  └────────────────────────────────────────────────┘│
 *   │  ┌─ content · padding p-4 ────────────────────────┐│
 *   │  │  Logo · Title (clamp-1)                        ││
 *   │  │  Description (clamp-2, min-h-10 reserves space)││
 *   │  │  Owner · stats                                 ││
 *   │  │  ┌─ action footer ────────────────────────┐   ││
 *   │  │  │ [Primary CTA — flex-grow] [▾ extras]    │   ││
 *   │  │  └─────────────────────────────────────────┘   ││
 *   │  └─────────────────────────────────────────────────┘│
 *   └────────────────────────────────────────────────────┘
 *
 * Title and description are wrapped in a `<Tooltip>` so AIKO offers a small
 * helper hint when hovered (full text + a contextual nudge). The tooltip is
 * subtle and non-intrusive — appears on hover after a 400ms delay.
 *
 * The action footer uses pretty's `Button` for the primary CTA and a
 * `DropdownMenu` chevron beside it for secondary actions, replacing the v3
 * inline-expanding command bar with a more conventional pattern.
 *
 * Empty-state fallbacks the lib handles for you:
 * - missing `coverImage` → branded gradient + initials (or logo)
 * - missing `name` → "Untitled" muted fallback
 * - missing `description` → reserved space stays empty (consistent card height)
 * - missing / zero stats → that meta item is silently skipped
 */

/**
 * Cover aspect ratio for every StudioCard surface. Studio BE cover uploads
 * land at 16:9 and consumer studio pages render them at 16:9; this constant
 * keeps the prop form (`aspect="16/9"`) and the Tailwind utility form
 * (`aspect-video` for compact / list / skeleton variants) bound to the same
 * source of truth so a future ratio change is a one-line edit. Pretty#166.
 */
export const STUDIO_CARD_COVER_ASPECT = "16/9" as const;
export const STUDIO_CARD_COVER_ASPECT_CLASS = "aspect-video" as const;

export type StudioVisibility = "public" | "private" | "team";

export interface StudioCardCreator {
  name: string;
  username?: string;
  avatar?: string;
}

export interface StudioCardEntity {
  id: string | number;
  name: string;
  description?: string;
  customUrl?: string;
  logo?: string;
  coverImage?: string;
  followers?: number;
  experiencesCount?: number;
  perTypeStats?: {
    aiNotes: number;
    aiExperts: number;
    quests: number;
    podcasts: number;
  };
  lastUpdated?: string;
  visibility?: StudioVisibility | string;
  isPremium?: boolean;
  owner?: StudioCardCreator;
  coCreators?: StudioCardCreator[];
  tags?: string[];
  subscribed?: boolean;
}

export interface StudioCardProps extends Omit<
  CardProps,
  "onClick" | "loading" | "children" | "view" | "hover"
> {
  studio: StudioCardEntity;
  view?: "grid" | "list";
  mode: "manage" | "explore" | "community";
  hover?: "lift" | "glow" | "none";

  /** Primary card action — also fires on whole-card click. */
  onPreview?: () => void;
  onManage?: () => void;
  onPromote?: () => void;
  onDelete?: () => void;
  onFollowToggle?: () => void;

  isFollowing?: boolean;
  followLoading?: boolean;
  userCanManageStudio?: boolean;
  userCanDeleteStudio?: boolean;
  isTeamWorkspace?: boolean;

  /**
   * Optional helper string AIKO whispers when the user hovers the title. If
   * omitted, the lib generates a sensible default ("Open this studio to see
   * all 12 experiences"). Set `aikoTooltip={false}` to opt out entirely.
   */
  aikoTooltip?: React.ReactNode | false;

  /**
   * Wrap the card in a `<BorderGlow>` halo. Use for "promoted",
   * "subscribed", or "now live" treatments where the card needs to read
   * as the special one in a grid.
   *
   * - `false` (default) — no halo.
   * - `true` — `<BorderGlow>` with library defaults (`color="accent"`,
   *   `intensity="default"`, `pulse={true}`). Reduced-motion users
   *   automatically get a static glow via `useReducedMotion()`.
   * - object — pass-through to `<BorderGlow>` props (`color`, `intensity`,
   *   `pulse`) for full control.
   *
   * Composition note: a pulsing halo plus `hover="glow"` doubles the
   * box-shadow channel — set `hover="none"` or `"lift"` when ramping
   * `glow` on so the two treatments don't fight. The default `hover="lift"`
   * already composes cleanly.
   */
  glow?: StudioCardGlow;
}

export type StudioCardGlow = boolean | Pick<BorderGlowProps, "color" | "intensity" | "pulse">;

function resolveGlow(
  glow: StudioCardGlow | undefined,
): Pick<BorderGlowProps, "color" | "intensity" | "pulse"> | null {
  if (!glow) return null;
  if (glow === true) return {};
  return glow;
}

const formatCount = (n: number | undefined): string => {
  if (!n || n <= 0) return "0";
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : String(n);
};

const studioInitials = (name: string | undefined): string => {
  if (!name) return "·";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function StudioCoverFallback({ name, logo }: { name: string; logo?: string }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-primary via-primary/70 to-accent/40 flex items-center justify-center">
      <div
        aria-hidden
        className="absolute -top-1/3 -left-1/4 size-[140%] rounded-full bg-white/10 blur-3xl"
      />
      {logo ? (
        <div className="relative size-16 sm:size-20 ring-2 ring-white/30 rounded-2xl overflow-hidden">
          <Image src={logo} alt="" rounded="lg" className="size-full" />
        </div>
      ) : (
        <span className="relative text-3xl sm:text-4xl font-semibold tracking-tight text-primary-foreground/85">
          {studioInitials(name)}
        </span>
      )}
    </div>
  );
}

/**
 * AIKO tooltip wrapper — used around title and description so a helper hint
 * surfaces on hover. Internal helper.
 */
function AikoTooltip({
  message,
  children,
}: {
  message: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="flex items-start gap-2">
            <AikoMark size="xs" aria-hidden />
            <span className="text-xs leading-relaxed">{message}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type StudioCardComponent = React.ForwardRefExoticComponent<
  StudioCardProps & React.RefAttributes<HTMLDivElement>
> & {
  Skeleton: typeof StudioCardSkeleton;
};

const StudioCardImpl = React.forwardRef<HTMLDivElement, StudioCardProps>(
  (
    {
      studio,
      view = "grid",
      mode,
      hover = "lift",
      onPreview,
      onManage,
      onPromote,
      onDelete,
      onFollowToggle,
      isFollowing,
      followLoading = false,
      userCanManageStudio = false,
      userCanDeleteStudio = false,
      isTeamWorkspace = false,
      aikoTooltip,
      glow,
      className,
      ...rest
    },
    ref,
  ) => {
    const {
      name,
      description,
      coverImage,
      logo,
      followers,
      experiencesCount,
      owner,
      coCreators,
      visibility,
    } = studio;
    const subscribed = isFollowing ?? studio.subscribed ?? false;
    const visKey = (visibility ?? "").toString().toLowerCase();
    const showVisibility = Boolean(visKey);

    // Default AIKO whisper — pre-formatted from the studio's own counts.
    const aikoMessage =
      aikoTooltip === false
        ? null
        : (aikoTooltip ??
          (experiencesCount && experiencesCount > 0
            ? `Open ${name || "this studio"} to explore ${experiencesCount} ${
                experiencesCount === 1 ? "AI experience" : "AI experiences"
              }${followers && followers > 0 ? ` from ${formatCount(followers)} subscriber${followers === 1 ? "" : "s"}` : ""}.`
            : `Open ${name || "this studio"} to see what's inside.`));

    const stopProp = (e: React.MouseEvent) => e.stopPropagation();
    const fire = (handler?: () => void) => (e: React.MouseEvent | Event) => {
      if ("stopPropagation" in e) e.stopPropagation();
      handler?.();
    };

    // Manage-mode: build the dropdown items list, silently dropping any not
    // wired or not permitted.
    const manageMenuItems: Array<React.ReactNode> = [];
    if (mode === "manage") {
      if (onPromote) {
        manageMenuItems.push(
          <DropdownMenuItem key="promote" onSelect={() => onPromote()}>
            <Megaphone />
            <span>Promote</span>
          </DropdownMenuItem>,
        );
      }
      if (onPreview) {
        manageMenuItems.push(
          <DropdownMenuItem key="preview" onSelect={() => onPreview()}>
            <Eye />
            <span>Preview</span>
          </DropdownMenuItem>,
        );
      }
      if (onDelete && userCanDeleteStudio) {
        if (manageMenuItems.length > 0) manageMenuItems.push(<DropdownMenuSeparator key="sep" />);
        manageMenuItems.push(
          <DropdownMenuItem key="delete" variant="destructive" onSelect={() => onDelete()}>
            <Trash2 />
            <span>Delete</span>
          </DropdownMenuItem>,
        );
      }
    }

    const glowConfig = resolveGlow(glow);
    const cardElement = (
      <Card
        ref={ref}
        view={view}
        hover={hover}
        onClick={onPreview}
        className={cn(view === "grid" && "h-full", onPreview && "cursor-pointer", className)}
        {...rest}
      >
        {/* Cover — sits flush at the top of the card. Pixel-effect overlay
            scatters muted pixels across the image on hover; combined with the
            image opacity dip, the bottom-left badge strip reads clearly even
            on busy photographic covers. */}
        <CardCover
          view={view}
          aspect={STUDIO_CARD_COVER_ASPECT}
          src={coverImage}
          alt={`${name || "Studio"} cover`}
          fallback={<StudioCoverFallback name={name} logo={logo} />}
          pixelEffect={view === "grid"}
        >
          {/* Bottom shade — deep gradient anchors the chip strip on any
              cover. Combined with the hover-time pixel scatter + image dim,
              the bottom-left badges stay legible across photographic and
              flat / illustrated covers alike. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
          />

          {/* Bottom-left scroll strip — visibility + premium */}
          {showVisibility || studio.isPremium ? (
            <div className="absolute inset-x-3 bottom-3 pointer-events-none">
              <div
                className={cn(
                  "flex items-center gap-2 overflow-x-auto pointer-events-auto",
                  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                  "[mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                  "[-webkit-mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                  "pr-8",
                )}
              >
                {showVisibility ? (
                  <VisibilityBadge
                    visibility={visKey}
                    appearance="overlay"
                    size="sm"
                    className="flex-shrink-0 shadow-sm"
                  />
                ) : null}
                {studio.isPremium ? (
                  <Badge
                    variant="premium"
                    className="flex-shrink-0 bg-background/85 backdrop-blur-sm shadow-sm"
                  >
                    Premium
                  </Badge>
                ) : null}
              </div>
            </div>
          ) : null}
        </CardCover>

        {/* Content section below image. `flex-1` so it absorbs remaining card
            height when the outer Card has `h-full` (grid view), keeping the
            action footer pinned to the bottom across siblings with varied
            content lengths. */}
        <div
          className={cn(
            "flex-1 flex flex-col gap-3 min-w-0",
            view === "grid" ? "p-4 sm:p-5" : "py-1 px-1",
          )}
        >
          {/* Logo + title row — community-card pattern. The logo lives in the
              title row (always visible, doesn't compete with cover content)
              rather than as a bottom-left-of-cover overlay. */}
          <div className="flex items-start gap-3 min-w-0">
            {logo || name ? (
              <Avatar
                src={logo}
                name={name}
                shape="square"
                size="default"
                className="rounded-xl flex-shrink-0 ring-1 ring-border/60"
              />
            ) : null}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {aikoMessage ? (
                <AikoTooltip message={aikoMessage}>
                  <CardTitle
                    as="h3"
                    size={view === "grid" ? "md" : "sm"}
                    clamp={view === "list" ? 1 : 2}
                  >
                    {name}
                  </CardTitle>
                </AikoTooltip>
              ) : (
                <CardTitle
                  as="h3"
                  size={view === "grid" ? "md" : "sm"}
                  clamp={view === "list" ? 1 : 2}
                >
                  {name}
                </CardTitle>
              )}
              {studio.customUrl ? (
                <Truncate as="span" className="text-xs text-primary/80">
                  {studio.customUrl}
                </Truncate>
              ) : null}
            </div>
          </div>

          {/* Description — reserved height so cards don't jitter when desc is
              missing. `<Truncate>` auto-tooltips when the text overflows; the
              invisible-bullet fallback (non-string ReactNode) never tooltips. */}
          <Truncate
            as="p"
            lines={view === "list" ? 1 : 2}
            className={cn(
              "text-sm leading-relaxed text-muted-foreground",
              view === "grid" && "min-h-[2.6rem]",
            )}
            tooltipBody={typeof description === "string" ? description : undefined}
          >
            {description ?? <span className="invisible">·</span>}
          </Truncate>

          {/* Meta row — owner / stats. `min-h-[1.25rem]` so wrap-to-2-lines
              vs no-wrap doesn't shift the action footer Y across siblings. */}
          <div className="flex min-h-[1.25rem] items-center gap-x-4 gap-y-1 flex-wrap text-xs sm:text-sm text-muted-foreground">
            {isTeamWorkspace && owner?.name ? <Truncate as="span">by {owner.name}</Truncate> : null}
            {coCreators && coCreators.length > 0 ? (
              <AvatarGroup
                size="xs"
                spacing="tight"
                maxVisible={3}
                items={coCreators.map((c) => ({
                  name: c.name,
                  src: c.avatar,
                  fallback: c.name.charAt(0).toUpperCase(),
                }))}
              />
            ) : null}
            {experiencesCount && experiencesCount > 0 ? (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <BookOpen className="size-3.5" />
                {experiencesCount}
                <span className="hidden sm:inline">
                  {experiencesCount === 1 ? "experience" : "experiences"}
                </span>
              </span>
            ) : null}
            {followers && followers > 0 ? (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <Users className="size-3.5" />
                {formatCount(followers)}
                <span className="hidden sm:inline">
                  {followers === 1 ? "subscriber" : "subscribers"}
                </span>
              </span>
            ) : null}
          </div>

          {/* Action footer. `mt-auto` pins it to the bottom of the
              flex column so the buttons land at the same Y across siblings
              with varied middle-row heights. */}
          {view === "grid" ? (
            <div className="mt-auto flex items-center gap-2 pt-1" onClick={stopProp}>
              {mode === "manage" ? (
                <>
                  {onManage && userCanManageStudio ? (
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={fire(onManage)}
                      aria-label={`Manage ${name}`}
                    >
                      <Settings />
                      <span>Manage</span>
                    </Button>
                  ) : onPreview ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={fire(onPreview)}
                      aria-label={`Preview ${name}`}
                    >
                      <Eye />
                      <span>Preview</span>
                    </Button>
                  ) : null}
                  {manageMenuItems.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="outline"
                          className="size-9 flex-shrink-0"
                          aria-label={`${name || "Studio"} more actions`}
                        >
                          <ChevronDown />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">{manageMenuItems}</DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </>
              ) : onFollowToggle ? (
                <Button
                  size="sm"
                  variant={subscribed ? "outline" : "default"}
                  className="flex-1"
                  isLoading={followLoading}
                  onClick={fire(onFollowToggle)}
                  aria-label={subscribed ? `Unsubscribe from ${name}` : `Subscribe to ${name}`}
                >
                  {subscribed ? <UserCheck /> : <UserPlus />}
                  <span>{subscribed ? "Subscribed" : "Subscribe"}</span>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
    );

    if (glowConfig) {
      return (
        <BorderGlow asChild {...glowConfig}>
          {cardElement}
        </BorderGlow>
      );
    }
    return cardElement;
  },
);
StudioCardImpl.displayName = "StudioCard";

/* -------------------------------------------------------------------------- */
/* StudioCard.Skeleton                                                        */
/* -------------------------------------------------------------------------- */

interface StudioCardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  view?: "grid" | "list";
}

export const StudioCardSkeleton = React.forwardRef<HTMLDivElement, StudioCardSkeletonProps>(
  ({ view = "grid", className, ...props }, ref) => {
    if (view === "list") {
      return (
        <div
          ref={ref}
          className={cn(
            // Notch surface (matches the loaded card): `nx-s` silhouette + a
            // clip-safe `.elev-soft` drop-shadow. No real border / box-shadow /
            // rounded-rect — the notch clip-path would slice them (#158); the
            // placeholder skips the keyline ring (it reads as plain chrome).
            nx.s,
            "relative bg-card elev-soft p-3 sm:p-4 flex items-start gap-4",
            className,
          )}
          {...props}
        >
          <Skeleton
            animation="shimmer"
            className={cn(
              STUDIO_CARD_COVER_ASPECT_CLASS,
              "w-32 sm:w-40 lg:w-48 rounded-lg flex-shrink-0",
            )}
          />
          <div className="flex-1 min-w-0 flex flex-col gap-2 py-1">
            <Skeleton animation="shimmer" className="h-5 w-3/5 rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
            <Skeleton animation="shimmer" className="h-3 w-1/3 rounded-md" />
          </div>
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn(
          // Notch surface (matches the loaded card): `nx-s` silhouette + a
          // clip-safe `.elev-soft` drop-shadow. `overflow-hidden` keeps the
          // cover skeleton inside the notch; no real border / box-shadow /
          // rounded-rect (the clip-path slices them, #158), no keyline ring.
          nx.s,
          "relative bg-card elev-soft",
          "overflow-hidden flex flex-col",
          className,
        )}
        {...props}
      >
        <Skeleton
          animation="shimmer"
          className={cn(STUDIO_CARD_COVER_ASPECT_CLASS, "w-full rounded-none")}
        />
        <div className="p-4 sm:p-5 flex flex-col gap-3">
          <Skeleton animation="shimmer" className="h-5 w-3/5 rounded-md" />
          <div className="flex flex-col gap-1.5">
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-4/5 rounded-md" />
          </div>
          <Skeleton animation="shimmer" className="h-3 w-1/2 rounded-md" />
          <div className="flex items-center gap-2 pt-1">
            <Skeleton animation="shimmer" className="h-9 flex-1 rounded-full" />
            <Skeleton animation="shimmer" className="size-9 rounded-full" />
          </div>
        </div>
      </div>
    );
  },
);
StudioCardSkeleton.displayName = "StudioCard.Skeleton";

/* -------------------------------------------------------------------------- */
/* StudioCardGrid                                                              */
/* -------------------------------------------------------------------------- */

export interface StudioCardGridProps extends React.HTMLAttributes<HTMLDivElement> {
  items: StudioCardEntity[];
  renderItem: (studio: StudioCardEntity, index: number) => React.ReactNode;
  animate?: boolean;
  /**
   * Column count at xl breakpoint. Mirrors CollectionCardGrid's API so the
   * three card grids (Studio / Experience / Collection) compose with parity
   * across surfaces. Default `"3"` (1 / 2 / 2 / 3 across sm / md / lg / xl).
   */
  columns?: "2" | "3" | "4";
  loading?: boolean;
  skeletonCount?: number;
}

export const StudioCardGrid = React.forwardRef<HTMLDivElement, StudioCardGridProps>(
  (
    {
      items,
      renderItem,
      animate = true,
      columns = "3",
      className,
      loading = false,
      skeletonCount = 6,
      ...props
    },
    ref,
  ) => {
    // Canonical breakpoints, identical across StudioCardGrid + ExperienceCardGrid
    // + CollectionCardGrid so the three card surfaces inherit one grid rhythm.
    const grid =
      columns === "2"
        ? "grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
        : columns === "4"
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6"
          : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6";
    if (loading) {
      return (
        <motion.div
          ref={ref}
          className={cn(grid, className)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          {...(props as React.ComponentProps<typeof motion.div>)}
        >
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <StudioCardSkeleton key={i} />
          ))}
        </motion.div>
      );
    }
    if (!animate) {
      return (
        <div ref={ref} className={cn(grid, className)} {...props}>
          {items.map((s, i) => (
            <React.Fragment key={s.id}>{renderItem(s, i)}</React.Fragment>
          ))}
        </div>
      );
    }
    return (
      <div ref={ref} className={cn(grid, className)} {...props}>
        <AnimatePresence mode="popLayout" initial={false}>
          {items.map((s, i) => (
            <motion.div
              key={s.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                layout: { type: "spring", stiffness: 260, damping: 30 },
                opacity: { duration: 0.25, ease: "easeOut" },
                y: { duration: 0.25, ease: "easeOut" },
                delay: Math.min(i * 0.03, 0.18),
              }}
            >
              {renderItem(s, i)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );
  },
);
StudioCardGrid.displayName = "StudioCardGrid";

// Suppress unused-import warning for icons we keep in scope for menu items.
void [Settings, Megaphone, Trash2, Eye, BookOpen, ChevronDown];
void CardDescription;

export const StudioCard = StudioCardImpl as StudioCardComponent;
StudioCard.Skeleton = StudioCardSkeleton;
